import { Box, TouchableBox, useLayoutContext, useTheme, AHIcon } from "arkh";
import React, { ReactNode } from "react";

import { Insets, Platform } from "react-native";

import { graphql, useFragment, useMutation } from "react-relay";
import { WidgetNotePageBackgroundSelector_widgetNotePage$key } from "./__generated__/WidgetNotePageBackgroundSelector_widgetNotePage.graphql";
import { WidgetNotePageBackgroundSelectorMutation } from "./__generated__/WidgetNotePageBackgroundSelectorMutation.graphql";
import { useEvent } from "../../../../common/useEvent";
import { WIDGET_NOTE_PAGE_COLORS } from "./WIDGET_NOTE_PAGE_COLORS";

type ColorCircleProps = {
  color: string;
  selected?: boolean;
  onPress: () => void;
  hitSlop?: Insets;
  children?: ReactNode;
};

const ColorCircle = (props: ColorCircleProps) => {
  const layout = useLayoutContext();
  return (
    <TouchableBox
      onPress={props.onPress}
      hitSlop={props.hitSlop}
      style={{
        marginRight: layout.space.medium,
        width: 26,
        height: 26,
        borderRadius: 13,
        borderWidth: 2,

        alignItems: "center",
        justifyContent: "center",
        backgroundColor: props.color,
      }}
      borderColor={props.selected ? "darkerText" : "transparent"}
    >
      {props.children}
    </TouchableBox>
  );
};

export const BackgroundSelector = (props: {
  page: WidgetNotePageBackgroundSelector_widgetNotePage$key;
  widgetId: string;
  openImageSelection: () => void;
  openImagePicker: () => void;
}) => {
  const theme = useTheme();

  const {
    backgroundColor,
    fontSize,
    fontStyle,
    id: pageId,
  } = useFragment(
    graphql`
      fragment WidgetNotePageBackgroundSelector_widgetNotePage on WidgetNotePage {
        id
        backgroundColor
        fontStyle
        fontSize
      }
    `,
    props.page
  );

  const [mutate] = useMutation<WidgetNotePageBackgroundSelectorMutation>(
    graphql`
      mutation WidgetNotePageBackgroundSelectorMutation(
        $noteId: ID!
        $pageId: ID!
        $value: WidgetNoteStyleInput!
      ) @raw_response_type {
        note(id: $noteId) {
          page(id: $pageId) {
            setStyle(style: $value) {
              id
              backgroundColor
              fontStyle
              fontSize
            }
          }
        }
      }
    `
  );

  const currentColor = backgroundColor ?? WIDGET_NOTE_PAGE_COLORS[0];

  const toggleBackgroundColor = useEvent(() => {
    const i = (WIDGET_NOTE_PAGE_COLORS as unknown as string).indexOf(
      currentColor
    );

    const nextIndex = i === -1 ? 1 : (i + 1) % WIDGET_NOTE_PAGE_COLORS.length;
    const nextColor = WIDGET_NOTE_PAGE_COLORS[nextIndex];

    const val = {
      fontSize,
      fontStyle,
      backgroundColor: nextColor,
    } as const;

    mutate({
      optimisticResponse: {
        note: {
          page: {
            setStyle: {
              id: pageId,
              ...val,
            },
          },
        },
      },
      variables: {
        pageId,
        noteId: props.widgetId,
        value: val,
      },
    });
  });

  return (
    <Box
      style={{
        flexDirection: "row",
      }}
    >
      <ColorCircle
        key={currentColor}
        color={currentColor}
        hitSlop={{
          left: 15,
          right: 15,
          top: 15,
          bottom: 15,
        }}
        selected
        onPress={toggleBackgroundColor}
      />
      {Platform.OS === "web" ? null : (
        <ColorCircle
          key="camera"
          color="surface"
          selected={false}
          onPress={props.openImageSelection}
          hitSlop={{
            left: 15,
            right: 15,
            top: 15,
            bottom: 15,
          }}
        >
          <AHIcon type="camera" forceSize={14} customColor={theme.darkerText} />
        </ColorCircle>
      )}
      <ColorCircle
        key="image"
        color="surface"
        selected={false}
        onPress={props.openImagePicker}
        hitSlop={{
          left: 15,
          right: 15,
          top: 15,
          bottom: 15,
        }}
      >
        <AHIcon type="image" forceSize={14} customColor={theme.darkerText} />
      </ColorCircle>
    </Box>
  );
};
